import { parseDate } from "./parseDate";

// Scalar fields spelled identically in both the Information tab's PATCH
// payload and the list/search endpoint's own card shape
// (TournamentListSerializer). `club`/`location` are NOT safe to add here -
// the Information tab sends their raw id, while the list card shows the
// resolved name string, so patching those blindly would replace a real
// club/location name with a stray UUID. (A club/location rename needs the
// same treatment but can't reuse this - out of scope here.)
export const SAFE_FIELDS = ["name", "start_date", "end_date", "grade", "gender", "format", "image", "width", "height"];

const pad2 = (n) => String(n).padStart(2, "0");

// Matches the "DD.MM" / "DD.MM-DD.MM" shape TournamentListSerializer's own
// date_range already returns (no year - see the samples in
// backend-tasks/2026-08-19-tournament-list-*.md). Recomputed client-side so
// an edited start/end date shows correctly right away wherever a card is
// already on screen (live list state, or a sessionStorage snapshot restored
// later) instead of displaying whatever date_range the backend computed
// before the edit until the next real fetch overwrites it.
const formatDateRange = (startVal, endVal) => {
    const start = parseDate(startVal);
    if (!start) return undefined;
    const end = parseDate(endVal) || start;
    const startLabel = `${pad2(start.getDate())}.${pad2(start.getMonth() + 1)}`;
    const sameDay = start.getFullYear() === end.getFullYear()
        && start.getMonth() === end.getMonth()
        && start.getDate() === end.getDate();
    if (sameDay) return startLabel;
    return `${startLabel}-${pad2(end.getDate())}.${pad2(end.getMonth() + 1)}`;
};

// Builds the patch to merge into one cached/in-memory tournament card: the
// SAFE_FIELDS subset of `fields` present, plus a freshly recomputed
// date_range whenever start_date/end_date changed. `fields` may be either a
// partial update (just the edited keys) or a full tournament object (e.g.
// tournamentSubscribers' own (id, next) payload) - either shape works, since
// only the SAFE_FIELDS keys actually present are read. Returns null when
// there's nothing relevant to apply.
export const patchedTournamentFields = (existing, fields) => {
    if (!fields) return null;
    const patch = {};
    SAFE_FIELDS.forEach(key => {
        if (key in fields) patch[key] = fields[key];
    });
    if (Object.keys(patch).length === 0) return null;

    if ("start_date" in patch || "end_date" in patch) {
        const nextStart = "start_date" in patch ? patch.start_date : existing?.start_date;
        const nextEnd = "end_date" in patch ? patch.end_date : existing?.end_date;
        const range = formatDateRange(nextStart, nextEnd);
        if (range) patch.date_range = range;
    }

    return patch;
};

export default patchedTournamentFields;
